import type { Extension, Schedule } from "@medplum/fhirtypes";
import { parseSchedulingParameters, SCHEDULING_PARAMS_URL, schedulingParamsToExtension } from "@repo/api";
import type { CreateScheduleInput, UpdateScheduleInput } from "@repo/api/schemas";
import { identifierSystem } from "@repo/fhir-systems";
import { BLOCK_HIGH_WATER_URL } from "@repo/fhir-systems/extensions";
import { SCHEDULE_ENTITY_ID_SYSTEM } from "@repo/fhir-systems/identifiers";
import type { ScheduleRow } from "../fhir/types";

export function applyScheduleFields(row: Schedule, input: UpdateScheduleInput): void {
  for (const field of ["active", "planningHorizon", "comment"] as const) {
    if (input[field] !== undefined) Object.assign(row, { [field]: input[field] });
  }
  for (const field of ["serviceCategory", "serviceType", "specialty"] as const) {
    if (input[field]) row[field] = input[field].map((coding) => ({ coding: [coding] }));
  }
  const params =
    input.schedulingParameters?.map(({ serviceId, ...param }) => ({
      ...param,
      service: { resourceType: "HealthcareService" as const, id: serviceId },
    })) ??
    parseSchedulingParameters(row.extension) ??
    [];
  row.extension = [
    ...(row.extension ?? []).filter((ext) => ext.url !== SCHEDULING_PARAMS_URL),
    ...params.map((param) => {
      const extension = schedulingParamsToExtension(
        {
          ...param,
          ...(input.availability ? { availability: input.availability } : {}),
          ...(input.timezone ? { timezone: input.timezone } : {}),
        },
        "",
      ) as unknown as Extension;
      const service = extension.extension?.find((part) => part.url === "service");
      if (service?.valueReference) delete service.valueReference.reference;
      return extension;
    }),
  ];
}

type ScheduleInput = Omit<CreateScheduleInput, "actorRef"> & { actorDisplay?: string } & (
    | { actorRef: string }
    | { actor: { type: "Practitioner" | "Location" | "Device"; id: string } }
  );

export function optimisticSchedule(input: ScheduleInput): ScheduleRow {
  const [actorType, actorId] = "actor" in input ? [input.actor.type, input.actor.id] : input.actorRef.split("/");
  if (actorType !== "Practitioner" && actorType !== "Location" && actorType !== "Device")
    throw new Error("Invalid schedule actor");
  const row: ScheduleRow = {
    resourceType: "Schedule",
    domainId: input.id,
    actorType,
    actorId,
    identifier: [{ system: SCHEDULE_ENTITY_ID_SYSTEM, value: input.id }],
    actor: [
      {
        identifier: { system: identifierSystem(actorType.toLowerCase()), value: actorId },
        display: input.actorDisplay,
      },
    ],
    active: input.active,
    extension: [{ url: BLOCK_HIGH_WATER_URL, valueInteger: input.initialBlocks?.length ?? 0 }],
  };
  applyScheduleFields(row, { ...input, id: input.id as UpdateScheduleInput["id"] });
  return row;
}
